import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Eye, EyeOff } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import { AuthenticationService } from '@/api/generated/services/AuthenticationService'
import type { PasswordChangeRequest } from '@/api/generated/models/PasswordChangeRequest'
import { useAlert } from '@/components/ui/Alert/exports'

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function PasswordChangeModal({
  isOpen,
  onClose,
  onSuccess
}: PasswordChangeModalProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useAlert()

  // Form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Form validation errors
  const [currentPasswordError, setCurrentPasswordError] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setCurrentPasswordError('')
      setNewPasswordError('')
      setConfirmPasswordError('')
      setShowCurrentPassword(false)
      setShowNewPassword(false)
      setShowConfirmPassword(false)
    }
  }, [isOpen])

  // Clear errors when user types
  useEffect(() => {
    setCurrentPasswordError('')
  }, [currentPassword])

  useEffect(() => {
    setNewPasswordError('')
  }, [newPassword])

  useEffect(() => {
    setConfirmPasswordError('')
  }, [confirmPassword])

  // Form validation
  const validateForm = () => {
    let isValid = true

    // Reset errors
    setCurrentPasswordError('')
    setNewPasswordError('')
    setConfirmPasswordError('')

    // Current password validation
    if (!currentPassword.trim()) {
      setCurrentPasswordError('현재 비밀번호를 입력해주세요.')
      isValid = false
    }

    // New password validation
    if (!newPassword.trim()) {
      setNewPasswordError('새 비밀번호를 입력해주세요.')
      isValid = false
    } else if (newPassword.length < 8) {
      setNewPasswordError('새 비밀번호는 최소 8자 이상이어야 합니다.')
      isValid = false
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      setConfirmPasswordError('새 비밀번호 확인을 입력해주세요.')
      isValid = false
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.')
      isValid = false
    }

    // Same password check
    if (currentPassword && newPassword && currentPassword === newPassword) {
      setNewPasswordError('새 비밀번호는 현재 비밀번호와 달라야 합니다.')
      isValid = false
    }

    return isValid
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const changeRequest: PasswordChangeRequest = {
        current_password: currentPassword.trim(),
        new_password: newPassword.trim(),
        confirm_password: confirmPassword.trim()
      }

      await AuthenticationService.appsAuthenticationViewsChangePassword({
        requestBody: changeRequest
      })

      showSuccess('비밀번호가 성공적으로 변경되었습니다.')
      onSuccess?.()
      handleClose()
    } catch (error: any) {
      console.error('Failed to change password:', error)

      // Parse error data structure
      const errorData = error.body || error.response?.data || error
      const errorMessages: string[] = []

      // Extract detailed error messages
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          errorData.detail.forEach((item: any) => {
            if (item?.msg) {
              errorMessages.push(item.msg)
            }
          })
        } else if (typeof errorData.detail === 'string') {
          errorMessages.push(errorData.detail)
        }
      }

      if (errorData?.message) {
        errorMessages.push(errorData.message)
      }

      if (errorData?.error) {
        errorMessages.push(errorData.error)
      }

      // If we have specific error messages, use them
      if (errorMessages.length > 0) {
        showError(errorMessages.join(' / '))
      } else {
        // Fallback to status-based messages
        if (error && typeof error === 'object' && 'status' in error) {
          switch (error.status) {
            case 400:
              showError('입력 데이터가 올바르지 않습니다.')
              break
            case 401:
              showError('현재 비밀번호가 올바르지 않습니다.')
              break
            case 500:
              showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
              break
            default:
              showError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          showError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setCurrentPasswordError('')
    setNewPasswordError('')
    setConfirmPasswordError('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    onClose()
  }

  const handleForgotPassword = () => {
    handleClose()
    navigate('/reset-password')
  }

  const isFormValid = currentPassword.trim().length > 0 &&
                     newPassword.length >= 8 &&
                     confirmPassword.trim().length > 0 &&
                     newPassword === confirmPassword

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[500px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              비밀번호 변경
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Current Password Field */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                현재 비밀번호 <span style={{ color: designTokens.colors.error }}>*</span>
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="현재 비밀번호를 입력해주세요."
                  size="medium"
                  state={currentPasswordError ? 'error' : 'default'}
                  disabled={isLoading}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {currentPasswordError && (
                <p
                  className="text-sm"
                  style={{
                    color: designTokens.colors.error,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {currentPasswordError}
                </p>
              )}
            </div>

            {/* New Password Field */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                새 비밀번호 <span style={{ color: designTokens.colors.error }}>*</span>
              </label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호를 입력해주세요. (최소 8자, 대소문자+숫자 포함)"
                  size="medium"
                  state={newPasswordError ? 'error' : 'default'}
                  disabled={isLoading}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {newPasswordError && (
                <p
                  className="text-sm"
                  style={{
                    color: designTokens.colors.error,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {newPasswordError}
                </p>
              )}
              <p
                className="text-xs"
                style={{
                  color: designTokens.colors.gray[4],
                  fontFamily: designTokens.typography.fontFamily.sans[0]
                }}
              >
                비밀번호는 최소 8자 이상이어야 합니다.
              </p>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                새 비밀번호 확인 <span style={{ color: designTokens.colors.error }}>*</span>
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력해주세요."
                  size="medium"
                  state={confirmPasswordError ? 'error' : 'default'}
                  disabled={isLoading}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {confirmPasswordError && (
                <p
                  className="text-sm"
                  style={{
                    color: designTokens.colors.error,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {confirmPasswordError}
                </p>
              )}
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="flex justify-center pt-4 pb-2">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm hover:underline transition-all duration-200"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                color: designTokens.colors.primary.DEFAULT
              }}
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              size="medium"
              color="white"
              onClick={handleClose}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              size="medium"
              color="dark"
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid}
            >
              {isLoading ? '변경 중...' : '변경'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}